/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amazon-athena",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Required permissions",
          "depth": 0,
          "id": "required-permissions"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your credentials",
          "depth": 1,
          "id": "configure-your-credentials"
        }, {
          "value": "Configure your Amazon Athena source",
          "depth": 1,
          "id": "configure-your-amazon-athena-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Data types",
          "depth": 0,
          "id": "data-types"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Performance considerations",
          "depth": 1,
          "id": "performance-considerations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink, SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet} = _components;
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in Amazon Athena and push it to downstream destinations. Connecting Hightouch to Amazon Athena requires some setup in both platforms. In Amazon, you need to make sure the AWS credentials you will use to connect Hightouch has the correct permissions."
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "The AWS user that you use to connect to Athena must have the following permissions:"
      }), "\n", _jsx(_components.p, {
        children: "Read and list permissions for Athena resources and execute permissions for Athena queries:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListWorkGroups"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListDataCatalogs"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListDatabases"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListTableMetadata"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "StartQueryExecution"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "GetQueryExecution"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "GetQueryResults"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "GetQueryResultsStream"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Read and write permissions for the output location in S3 to both write the results to S3, read them back, and perform cleanup:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "PutObject"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListBucket"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListBucketMultipartUploads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ListMultipartUploadParts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "GetBucketLocation"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "GetObject"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "AbortMultipartUpload"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "DeleteObject"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See the AWS managed policy ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/aws-managed-policy/latest/reference/AmazonAthenaFullAccess.html",
          children: _jsx(_components.code, {
            children: "AmazonAthenaFullAccess"
          })
        }), "\nfor reference."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you are using Lake Formation for ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/lake-formation/latest/dg/managing-permissions.html",
            children: "managing permissions"
          }), " you need to ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/lake-formation/latest/dg/granting-database-permissions.html",
            children: "grant database permissions"
          }), " to the AWS user that you use to connect to Athena, otherwise no databases load when configuring the source."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Amazon Athena"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-credentials",
        children: "Configure your credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["You can either select AWS credentials you've previously configured in Hightouch or choose to add ", _jsx(_components.strong, {
          children: "New credentials"
        }), " directly from this page."]
      }), "\n", _jsxs(_components.p, {
        children: ["The credentials must be for a user who has permission to access Athena and the S3 output location. Refer to the ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "required permissions"
        }), " section for permission details and to the ", _jsx(_components.a, {
          href: "/security/aws",
          children: "AWS credential configuration"
        }), " docs for credential setup information."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-amazon-athena-source",
        children: "Configure your Amazon Athena source"
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Athena ", _jsx(_components.strong, {
            children: "workgroup"
          }), " you previously set up in AWS."]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Query output location"
          }), "—you only need to enter this if your workgroup doesn't have an output location configured. This is an S3 path."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data catalog"
          }), " name"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), " name"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Advanced) ", _jsx(_components.strong, {
            children: "VPC connection endpoint"
          }), ". To connect through a private VPC endpoint, ", _jsx(PylonLink, {
            pylonText: "Hi, I need the VPC connection endpoint for Amazon Athena.",
            linkText: "contact Hightouch"
          }), " and we can provide the endpoint for this step."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Workgroup and data catalog options populate depending on your AWS credentials. If you don't see the expected values, confirm your AWS credentials and click ", _jsx(_components.strong, {
          children: "Refresh"
        }), ". Your database options populate depending on your selected data catalog."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "Amazon Athena",
        supportLightningEngineMigration: "false"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsx(EngineComparisonTable, {
        source: "Amazon Athena",
        supportLightningEngineMigration: "false"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-setup",
        children: "Lightning engine setup"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch uses two schemas (", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), ") for storing logs of previously synced data. Hightouch must be able to read and write to these schemas, but the specific schema names might vary."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Required for the Lightning engine"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_planner;\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- For sync logs, optional"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "<"
          }), "catalog", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">"
          }), ".hightouch_audit;\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "Amazon Athena"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from Athena."]
      }), "\n", _jsx(_components.p, {
        children: "The Athena source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/looker-models",
            children: "Looker Looks"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "data-types",
        children: "Data types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch parses most Athena data types into JavaScript types before sending them to your destination. Hightouch leaves the following Athena data types as strings:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BIGINT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "ARRAY"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "MAP"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Read more about Athena data types in the ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/athena/latest/ug/querying-iceberg-supported-data-types.html",
          children: "Athena documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "performance-considerations",
        children: "Performance considerations"
      }), "\n", _jsx(_components.p, {
        children: "The following Service Quota changes to Amazon Athena are recommended in order to support fast and reliable syncs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Active DML queries: 400", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["To support concurrent ", _jsx(_components.code, {
                children: "CREATE TABLE AS"
              }), " queries which are ", _jsx(_components.a, {
                href: "https://docs.aws.amazon.com/athena/latest/ug/create-table-as.html",
                children: "DML statements"
              }), "."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
